/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container, Row, Col } from "react-awesome-styled-grid";
import { Box, Heading, Loader } from "@nordcloud/gnui";
import { QueryPagination, Table } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { FilteredSearch } from "~/components/Search/FilteredSearch";
import { useUserConsumer } from "~/context/User/User";
import { isSuperAdmin, ROUTES } from "~/routing";
import { useAxios, useQueryState } from "~/tools";
import useSortParams from "~/tools/hooks/useSortParams";
import { EmployeesListColumns } from "./EmployeesList";

function EmployeesPage() {
  const [isLoading, setIsLoading] = React.useState(true);

  const [totalCount, setTotalCount] = React.useState(0);
  const [employeeFiltered, setEmployeeFiltered] = React.useState([]);
  const [sortParams, setSorting] = useSortParams("lastName");
  const [{ limit, page, query }] = useQueryState();

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const { role } = useUserConsumer();
  const isSuperRole = isSuperAdmin(role);

  const fetchEmployees = React.useCallback(async () => {
    try {
      const params = { ...sortParams, limit, page, query };
      const { data } = await Axios().get("/employees", params);
      setTotalCount(data?.totalCount);
      setEmployeeFiltered(data?.employees);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, limit, page, query, sortParams, showError]);

  React.useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const columns = React.useMemo(() => EmployeesListColumns(fetchEmployees), [
    fetchEmployees,
  ]);

  const trimColumns = isSuperRole ? columns : columns.slice(0, 3);

  return (
    <Container>
      <Row>
        <Col>
          <Box
            css={{ display: "flex", alignItems: "center" }}
            spacing="spacing04"
          >
            <Heading level={2}>List of Employees</Heading>
          </Box>
          <Box spacing="spacing04">
            <Box boxStyle="lightGrey" spacing="spacing04">
              <FilteredSearch
                mode="employees"
                endpoint="employees"
                redirectLink={ROUTES.employees.index}
                options={{
                  keys: ["id", "email", "firstName", "lastName", "permission"],
                }}
              />
            </Box>
            {isLoading ? (
              <Loader inContent />
            ) : (
              <>
                <Table
                  columns={trimColumns}
                  data={employeeFiltered || []}
                  defaultSort={[{ id: "lastName", desc: false }]}
                  serverSideSort={setSorting}
                />
                <QueryPagination count={totalCount} />
              </>
            )}
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export { EmployeesPage };
